import { type HttpClientConfig, type HttpClientInterface } from 'core/infra/http/HttpClient'

import { type RawTenderOfferListResponse } from '../domain/TenderOffer'
import { mapApiToTenderOfferList } from './TenderAdapter'

type PendingTendersRepositoryDependencies = {
  httpClient: HttpClientInterface
}

type AcceptTenderResponse = {
  tender_uuid: string
  shipment_uuid: string
  shipment_carrier_1xp_link: string
}

type AttachmentLink = {
  file_url: string
}

export type AttachmentRequestBody = { quoteUuid: string; attachmentId: number }

const BASE_URL = '/carrier/tenders/pending'

export class PendingTendersRepository {
  private httpClient: HttpClientInterface

  constructor({ httpClient }: PendingTendersRepositoryDependencies) {
    this.httpClient = httpClient
  }

  async getPendingTenders({ config }: { config?: Partial<HttpClientConfig> }) {
    const response = await this.httpClient.get<RawTenderOfferListResponse>(`${BASE_URL}`, config)

    return mapApiToTenderOfferList(response.data)
  }

  async acceptTender({
    tenderUuid,
    config,
  }: {
    tenderUuid: string
    config?: Partial<HttpClientConfig>
  }): Promise<AcceptTenderResponse> {
    const endpoint = `/carrier/tenders/tender-confirmation/${tenderUuid}/accept`

    const response = await this.httpClient.patch<AcceptTenderResponse>(endpoint, undefined, config)

    return response.data
  }

  async rejectTender({
    tenderUuid,
    rejectReason,
    config,
  }: {
    tenderUuid: string
    rejectReason?: string
    config?: Partial<HttpClientConfig>
  }): Promise<void> {
    const endpoint = `/carrier/tenders/tender-confirmation/${tenderUuid}/reject`

    const payload = rejectReason ? { reject_reason: rejectReason } : undefined

    await this.httpClient.patch(endpoint, payload, config)
  }

  async getAttachmentURL(
    { quoteUuid, attachmentId }: AttachmentRequestBody,
    config?: Partial<HttpClientConfig>
  ): Promise<string> {
    const endpoint = `spot_quotes/${quoteUuid}/attachments/${attachmentId}/download`

    const response = await this.httpClient.get<AttachmentLink>(endpoint, config)

    return response.data.file_url
  }
}
